import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { daysUntil } from '@/pages/home/widgets/format';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * 현장 준비 — 착공 앞 · 준공 앞에 <b>끌 수 없이</b> 뜨는 알림 (DOC-61 §11 · V176).
 *
 * <h2>왜 한 칸인가</h2>
 * 원래 「착공 준비」 하나였고, 준공 전 용역 발주가 붙으면서 <b>같은 칸에</b> 넣었다
 * (사용자 지시 2026-09-01 — "하나로 현장 위젯같은거로 합쳐서"). 둘로 두면 홈에 비슷하게 생긴
 * 네모가 나란히 뜨고, 그러면 <b>둘 다 대충 읽힌다.</b> 묻는 것도 하나다 — 「지금 내 현장에서
 * 챙길 것이 있나」.
 *
 * <h2>두 갈래를 섞지는 않는다</h2>
 * 한 칸 안에서 <b>줄로 가른다.</b> 착공은 계약 날인 ~ 착공 사이 몇 주짜리이고, 준공 용역은 넉 달
 * 앞의 일이라 급한 정도가 다르다. 한 목록에 섞어 기한 순으로 세우면 「D-3 세움터」 와
 * 「D-100 건설지」 가 나란히 서서, 급한 줄이 급해 보이지 않는다.
 *
 * <h2>저절로 사라진다</h2>
 * 착공은 착공일이 찍히면, 준공 용역은 <b>발주일이 찍히면</b> 그 줄이 빠진다. 사람이 「끝났다」 를
 * 눌러 닫게 두면 아무도 안 누른 현장이 몇 달째 빨갛게 떠 있고, 그때부터 그 빨강은 아무 뜻이 없어진다.
 *
 * <h2>여기서 고치지 않는다</h2>
 * 누르면 그 현장의 해당 탭으로 간다. 위젯에서 바로 값을 바꾸게 하면 같은 값을 고치는 곳이 둘이
 * 되고, 언젠가 한 곳만 고쳐진다.
 *
 * <h2>세 번째 칸 — 진행 현장 (사용자 지시 2026-10-06 · 화면 글씨는 「○○ 알림」 만)</h2>
 * 위젯 이름도 이때 「현장 준비」 에서 「현장 알림」 으로 바꿨다 — 진행 중인 일까지 담게 됐다.
 * 착공 뒤에도 남은 착공서류 · 계획일을 넘긴 공정(전 공정) · 발주 계획일을 넘긴 하도급 · 선임 신고
 * 지연 · 법정인원 공석. 받는 사람은 <b>그 현장 담당자</b>뿐이다(배치 · F/U · 서류 담당). 위 두 칸처럼
 * 실적을 적으면 저절로 빠진다.
 */

type LaunchRow = {
  siteId: string;
  siteName: string;
  scheduledStartOn: string | null;
  seumteoOpenedOn: string | null;
  fireReportDueOn: string | null;
  fireReportedOn: string | null;
  handoverDoneOn: string | null;
  missingRequired: number;
  earliestDueOn: string | null;
  totalItems: number;
};

type DeadlineRow = {
  siteId: string;
  siteName: string;
  scheduledCompletionDate: string | null;
  /** 아직 발주·갱신되지 않은 줄 수. */
  openCount: number;
  earliestDueOn: string | null;
  /** 가장 급한 줄의 이름("준공용역"·"건설지"). 배지에 그대로 적는다. */
  earliestName: string | null;
};

type OverdueGroup = {
  count: number;
  /** 가장 오래 밀린 줄의 기한. */
  earliestDueOn: string | null;
  earliestName: string | null;
};

/** 담당 현장의 기한 넘긴 일 (사용자 지시 2026-10-06 · `SiteOverdueService`). */
type OverdueRow = {
  siteId: string;
  siteName: string;
  documents: OverdueGroup;
  wbs: OverdueGroup;
  orders: OverdueGroup;
  dutyReports: OverdueGroup;
  staffing: { role: string; vacancy: number }[];
};

export function SitePrepWidget() {
  const launches = useQuery({
    queryKey: ['site-launches-open'],
    queryFn: () => apiFetch<LaunchRow[]>('/v1/sites/launches/open'),
    staleTime: 60_000,
  });
  const deadlines = useQuery({
    queryKey: ['site-deadlines-open'],
    queryFn: () => apiFetch<DeadlineRow[]>('/v1/sites/deadlines/open'),
    staleTime: 60_000,
  });
  const overdueQuery = useQuery({
    queryKey: ['site-overdue-open'],
    queryFn: () => apiFetch<OverdueRow[]>('/v1/sites/overdue/open'),
    staleTime: 60_000,
  });

  const launchRows = launches.data ?? [];
  const deadlineRows = deadlines.data ?? [];
  const overdueRows = overdueQuery.data ?? [];

  return (
    <WidgetFrame
      title="현장 알림"
      icon="clipboard"
      /*
        둘 다 기다릴 때만 「불러오는 중」 이다. 한쪽이 먼저 오면 그것부터 보여 준다 — 착공 현장이
        있는 사람이 준공 질의 때문에 빈 화면을 보는 이유가 없다.

        오류도 같다: 한쪽이 죽어도 다른 쪽은 그린다. 둘 다 죽었을 때만 오류를 말한다.
      */
      pending={launches.isPending && deadlines.isPending && overdueQuery.isPending}
      error={launches.error && deadlines.error && overdueQuery.error ? launches.error : null}
      empty={launchRows.length === 0 && deadlineRows.length === 0 && overdueRows.length === 0}
      emptyText="챙길 현장이 없습니다."
    >
      <div className="space-y-3">
        {launchRows.length > 0 && (
          <Section title="착공 준비">
            {launchRows.map((row) => (
              <Link
                key={row.siteId}
                to={`/sites/pmis?site=${row.siteId}&tab=permit`}
                className="block rounded-lg px-3 py-2 hover:bg-slate-50"
                style={{ border: '1px solid var(--border)' }}
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-ui-md" style={{ fontWeight: 600 }}>
                    {row.siteName}
                  </span>
                  <StartBadge scheduledStartOn={row.scheduledStartOn} />
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {/*
                    소방착공 신고 — 「소방착공 전까지 신고 대상 알림」(사용자 지시). 대상 여부는
                    우리가 판정하지 않으므로, 기한이 적혀 있지 않으면 아무 말도 하지 않는다.
                  */}
                  <FireBadge dueOn={row.fireReportDueOn} reportedOn={row.fireReportedOn} />

                  {row.missingRequired > 0 ? (
                    <span
                      className={overdue(row.earliestDueOn) ? 'badge badge-sm badge-red' : 'badge badge-sm badge-yellow'}
                      title={row.earliestDueOn ? `가장 이른 기한 ${row.earliestDueOn}` : undefined}
                    >
                      미비서류 {row.missingRequired}건
                    </span>
                  ) : row.totalItems === 0 ? (
                    // 0건과 「목록이 아직 없다」 는 다른 상태다. 접으면 화면이 「다 됐다」 로 그린다.
                    <span className="badge badge-gray badge-sm">서류 목록 없음</span>
                  ) : (
                    <span className="badge badge-green badge-sm">서류 완료</span>
                  )}

                  {!row.seumteoOpenedOn && <span className="badge badge-yellow badge-sm">세움터 미개설</span>}
                  {!row.handoverDoneOn && <span className="badge badge-gray badge-sm">인계 전</span>}
                </div>
              </Link>
            ))}
          </Section>
        )}

        {deadlineRows.length > 0 && (
          <Section title="준공 전 용역 발주">
            {deadlineRows.map((row) => (
              <Link
                key={row.siteId}
                /*
                  <b>수정 창까지 열어 준다</b>(`edit=1`). 발주일을 적는 자리가 그 창이라,
                  현장만 열어 두면 사람은 어디서 처리하는지 또 찾는다 — 그 한 번의 「찾기」 가
                  「나중에」 가 된다.
                */
                to={`/sites/pmis?site=${row.siteId}&edit=1`}
                className="block rounded-lg px-3 py-2 hover:bg-slate-50"
                style={{ border: '1px solid var(--border)' }}
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-ui-md" style={{ fontWeight: 600 }}>
                    {row.siteName}
                  </span>
                  <CompletionBadge scheduledCompletionDate={row.scheduledCompletionDate} />
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {/*
                    이름을 적는다. 「미발주 2건」 만으로는 무엇을 발주해야 하는지 몰라 결국 눌러
                    확인해야 하고, 그 한 번의 클릭이 「나중에」 가 된다.
                  */}
                  <span
                    className={overdue(row.earliestDueOn) ? 'badge badge-sm badge-red' : 'badge badge-sm badge-yellow'}
                    title={row.earliestDueOn ? `착수 기한 ${row.earliestDueOn}` : undefined}
                  >
                    {row.earliestName ?? '용역'} 미발주
                    {row.openCount > 1 && ` 외 ${row.openCount - 1}건`}
                  </span>
                </div>
              </Link>
            ))}
          </Section>
        )}

        {overdueRows.length > 0 && (
          <Section title="진행 현장">
            {overdueRows.map((row) => (
              /*
                줄 전체를 링크로 두지 않는다 — 갈래마다 고치는 탭이 다르다. 배지 하나가 그 탭으로 가는
                문이고, 「공정 알림」 을 눌렀는데 착공서류 탭이 열리면 사람은 다시 찾아 헤맨다.
              */
              <div
                key={row.siteId}
                className="rounded-lg px-3 py-2"
                style={{ border: '1px solid var(--border)' }}
              >
                <p className="truncate text-ui-md" style={{ fontWeight: 600 }}>
                  {row.siteName}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <OverdueBadge siteId={row.siteId} tab="permit" label="서류 알림" group={row.documents} />
                  <OverdueBadge siteId={row.siteId} tab="progress" label="공정 알림" group={row.wbs} />
                  <OverdueBadge siteId={row.siteId} tab="subcontract" label="발주 알림" group={row.orders} />
                  <OverdueBadge siteId={row.siteId} tab="permit" label="선임 신고 알림" group={row.dutyReports} />
                  {row.staffing.map((gap) => (
                    <Link
                      key={gap.role}
                      to={`/sites/pmis?site=${row.siteId}&tab=org`}
                      className="badge badge-sm badge-red"
                    >
                      인원 알림 · {gap.role} {gap.vacancy}명
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </Section>
        )}
      </div>
    </WidgetFrame>
  );
}

/**
 * 알림 갈래 하나. 가장 이른 기한의 줄 이름을 함께 적는다 — 「공정 알림 12건」 만으로는 무엇이
 * 늦었는지 몰라 결국 눌러 보게 되고, 그 한 번이 「나중에」 가 된다.
 */
function OverdueBadge({
  siteId,
  tab,
  label,
  group,
}: {
  siteId: string;
  tab: string;
  label: string;
  group: OverdueGroup;
}) {
  if (group.count === 0) return null;
  return (
    <Link
      to={`/sites/pmis?site=${siteId}&tab=${tab}`}
      className="badge badge-sm badge-red"
      title={group.earliestDueOn ? `기한 ${group.earliestDueOn}` : undefined}
    >
      {group.earliestName
        ? `${label} · ${group.earliestName}${group.count > 1 ? ` 외 ${group.count - 1}건` : ''}`
        : `${label} · ${group.count}건`}
    </Link>
  );
}

/**
 * 갈래 제목. 한쪽만 있을 때도 <b>그대로 붙인다</b> — 착공 줄만 있는 날과 준공 줄만 있는 날에
 * 같은 위치의 같은 글자가 다른 뜻이 되면, 사람은 위젯을 매번 처음부터 읽는다.
 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)' }}>{title}</p>
      {children}
    </div>
  );
}

/** 착공까지 남은 날. 예정일이 없으면 그 사실을 말한다 — 없는 것과 여유로운 것은 다르다. */
function StartBadge({ scheduledStartOn }: { scheduledStartOn: string | null }) {
  if (!scheduledStartOn) {
    return <span className="badge badge-gray flex-shrink-0 badge-sm">착공일 미정</span>;
  }
  const left = daysUntil(scheduledStartOn);
  if (left < 0) {
    return (
      <span className="badge badge-red flex-shrink-0 badge-sm" title={`착공 예정 ${scheduledStartOn}`}>
        착공 예정일 경과
      </span>
    );
  }
  return (
    <span
      className={left <= 14 ? 'badge badge-sm badge-yellow flex-shrink-0' : 'badge badge-sm badge-blue flex-shrink-0'}
      title={`착공 예정 ${scheduledStartOn}`}
    >
      착공 D-{left}
    </span>
  );
}

/**
 * 준공까지 남은 날.
 *
 * <p>착공과 달리 <b>「미정」 이 뜰 일이 거의 없다</b> — 준공예정일이 없으면 애초에 기한이 서지
 * 않아 이 목록에 오지 않는다. 그래도 판정은 한다: 예정일을 지운 뒤에도 줄은 남아 있다.
 */
function CompletionBadge({ scheduledCompletionDate }: { scheduledCompletionDate: string | null }) {
  if (!scheduledCompletionDate) {
    return <span className="badge badge-gray flex-shrink-0 badge-sm">계약 준공일 미정</span>;
  }
  const left = daysUntil(scheduledCompletionDate);
  if (left < 0) {
    return (
      <span className="badge badge-red flex-shrink-0 badge-sm" title={`계약 준공 ${scheduledCompletionDate}`}>
        계약 준공일 경과
      </span>
    );
  }
  return (
    <span
      className={left <= 60 ? 'badge badge-sm badge-yellow flex-shrink-0' : 'badge badge-sm badge-blue flex-shrink-0'}
      title={`계약 준공 ${scheduledCompletionDate}`}
    >
      준공 D-{left}
    </span>
  );
}

/**
 * 소방착공 신고.
 *
 * 기한이 비어 있으면 <b>아무 말도 하지 않는다.</b> 대상 여부는 연면적·용도가 정하는데 우리는 그
 * 판정을 하지 않기로 했다(V118 §3.1) — 짐작해서 「신고하세요」 라고 말하면 그 말에 책임이 붙는다.
 */
function FireBadge({ dueOn, reportedOn }: { dueOn: string | null; reportedOn: string | null }) {
  if (reportedOn) {
    return <span className="badge badge-green badge-sm">소방 신고 완료</span>;
  }
  if (!dueOn) return null;

  const left = daysUntil(dueOn);
  if (left < 0) {
    return (
      <span className="badge badge-red badge-sm" title={`신고 기한 ${dueOn}`}>
        소방 신고 {-left}일 초과
      </span>
    );
  }
  return (
    <span className="badge badge-yellow badge-sm" title={`신고 기한 ${dueOn}`}>
      소방 신고 D-{left}
    </span>
  );
}

function overdue(dueOn: string | null): boolean {
  return dueOn != null && daysUntil(dueOn) < 0;
}
